import {ConfirmationDialog} from '@primer/react'
import {Fragment} from 'react'

import type {FieldsSectionFieldValues$data} from './__generated__/FieldsSectionFieldValues.graphql'
import {LABELS} from './constants/labels'
import {IssueFieldDateEditor} from './IssueFieldDateEditor'
import {IssueFieldNumberEditor} from './IssueFieldNumberEditor'
import {IssueFieldSingleSelectEditor} from './IssueFieldSingleSelectEditor'
import {IssueFieldTextEditor} from './IssueFieldTextEditor'

type FieldValue = NonNullable<NonNullable<FieldsSectionFieldValues$data['issueFieldValues']>['nodes']>[number]
type FieldValueField = NonNullable<
  NonNullable<NonNullable<NonNullable<FieldsSectionFieldValues$data['issueFieldValues']>['nodes']>[number]>['field']
>
type FieldValueValues = Omit<FieldValue, 'field' | 'value'>

export type SafeFieldValue = FieldValue & {
  field: FieldValueField & {id: string}
  value: string | FieldValueValues
}

type getEditorProps<T> = {
  fieldValue: SafeFieldValue
  readonly: boolean
  hideDivider: boolean
  onCommit: IssueFieldOnCommitFn<T>
}

export type IssueFieldOnCommitFn<T> = (fieldId: string, value: T) => void

export type IssueFieldEditorsProps<T> = {
  errorPresent: boolean
  setErrorPresent: () => void
} & getEditorProps<T>

export function getIssueFieldEditor<T>({
  errorPresent,
  setErrorPresent,
  fieldValue,
  ...props
}: IssueFieldEditorsProps<T>) {
  if (!fieldValue.field) return null

  const editor = getEditor({fieldValue, ...props})

  return (
    <Fragment key={fieldValue.field.name}>
      {editor}
      {errorPresent && <ErrorDialog onClose={setErrorPresent} message={LABELS.mutationErrorMessage} />}
    </Fragment>
  )
}

export function getEditor<T>({fieldValue, readonly, hideDivider, onCommit}: getEditorProps<T>) {
  const isCleared = fieldValue.value === ''

  switch (fieldValue.field.dataType) {
    case 'TEXT':
      return (
        <IssueFieldTextEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          initialValue={fieldValue.value || ''}
          readonly={readonly}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          hideDivider={hideDivider}
        />
      )
    case 'SINGLE_SELECT':
      return (
        <IssueFieldSingleSelectEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          initialValue={{
            name: isCleared || !fieldValue?.name ? '' : fieldValue.name,
            color: isCleared || !fieldValue?.color ? '' : fieldValue.color,
            description: isCleared || !fieldValue?.description ? '' : fieldValue.description,
          }}
          readonly={readonly}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          hideDivider={hideDivider}
        />
      )
    case 'DATE': {
      const dateValue = fieldValue.value ? new Date(fieldValue.value) : null
      return (
        <IssueFieldDateEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          value={dateValue}
          readonly={readonly}
          onCommit={(fieldId, value) => onCommit(fieldId, value as T)}
          hideDivider={hideDivider}
        />
      )
    }
    case 'NUMBER':
      return (
        <IssueFieldNumberEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          // Default to 0 if no value is present.
          // This should be fixed as part of this issue: https://github.com/github/issues/issues/17544
          initialValue={fieldValue.numberValue?.toString() || '0'}
          readonly={readonly}
          onCommit={(fieldId, value) => {
            const numericValue = Number(value)
            onCommit(fieldId, numericValue as T)
          }}
          hideDivider={hideDivider}
        />
      )
    default:
      throw new Error(`Unsupported field type: ${fieldValue.field.dataType}`)
  }
}

// Generic error dialog
export function ErrorDialog({onClose, message}: {onClose: () => void; message: string}) {
  return (
    <ConfirmationDialog title="Error" confirmButtonContent="OK" confirmButtonType="primary" onClose={onClose}>
      {message}
    </ConfirmationDialog>
  )
}

try{ ErrorDialog.displayName ||= 'ErrorDialog' } catch {}